// Plain-language wording for tasks and reply tracking (CLAUDE.md invariant 8): "due today",
// "waiting on Lena · 4 days", "waiting for you since Mon"; never "overdue", never a red count.
// Pure: callers pass `now`, and tests pin the locale and time zone.
import type { TaskListView, TaskView, ThreadStatusView } from "@shared/api";
import type { TimeFormatOptions } from "@web/lib/time";

const DAY = 86_400_000;

function toDate(value: string | Date): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** YYYY-MM-DD of `date` in the given zone. */
function dayKey(date: Date, opts: TimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: opts.timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function dayNumber(key: string): number {
  const [y, m, d] = key.split("-").map(Number);
  return Math.round(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) / DAY);
}

/** Calendar days from today to `target` in the reader's zone (0 today, 1 tomorrow, -1 yesterday). */
export function calendarDayDiff(
  target: string | Date,
  now: number | Date,
  opts: TimeFormatOptions = {},
): number {
  const date = toDate(target);
  if (!date) return 0;
  const nowDate = now instanceof Date ? now : new Date(now);
  return dayNumber(dayKey(date, opts)) - dayNumber(dayKey(nowDate, opts));
}

function weekday(date: Date, opts: TimeFormatOptions): string {
  return new Intl.DateTimeFormat(opts.locale, { weekday: "short", timeZone: opts.timeZone }).format(
    date,
  );
}

function monthDay(date: Date, opts: TimeFormatOptions): string {
  return new Intl.DateTimeFormat(opts.locale, {
    month: "short",
    day: "numeric",
    timeZone: opts.timeZone,
  }).format(date);
}

export function timeOfDay(date: Date, opts: TimeFormatOptions = {}): string {
  return new Intl.DateTimeFormat(opts.locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: opts.timeZone,
  }).format(date);
}

/** "today", "tomorrow", "yesterday", "Fri" (within a week either way), else "Oct 16". */
export function relativeDay(
  target: string | Date,
  now: number | Date,
  opts: TimeFormatOptions = {},
): string {
  const date = toDate(target);
  if (!date) return "";
  const diff = calendarDayDiff(date, now, opts);
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff === -1) return "yesterday";
  if (Math.abs(diff) < 7) return weekday(date, opts);
  return monthDay(date, opts);
}

/**
 * When a task is due, in words: "due today at 3:00 PM", "due tomorrow", "due Fri", "due Oct 16".
 * A date that has passed reads "waiting for you since Mon": never "overdue".
 */
export function dueLabel(
  task: Pick<TaskView, "dueAt" | "dueKind">,
  now: number | Date,
  opts: TimeFormatOptions = {},
): string | null {
  if (!task.dueAt) return null;
  const due = toDate(task.dueAt);
  if (!due) return null;
  const diff = calendarDayDiff(due, now, opts);
  if (diff < 0) return `waiting for you since ${relativeDay(due, now, opts)}`;
  const time = task.dueKind === "datetime" ? ` at ${timeOfDay(due, opts)}` : "";
  return `due ${relativeDay(due, now, opts)}${time}`;
}

export function firstName(
  person: { name?: string; email?: string } | null | undefined,
): string | null {
  const name = person?.name?.trim().split(/\s+/)[0];
  if (name) return name;
  const email = person?.email?.trim();
  return email ? (email.split("@")[0] ?? email) : null;
}

function daysSpan(days: number): string {
  if (days <= 0) return "since today";
  return days === 1 ? "1 day" : `${days} days`;
}

/** "waiting on Lena · 4 days" for waiting-on tasks; null for ordinary to-dos. */
export function waitingOnLabel(
  task: Pick<TaskView, "kind" | "waitingOn" | "createdAt">,
  now: number,
): string | null {
  if (task.kind !== "waiting_on") return null;
  const who = firstName(task.waitingOn) ?? "someone";
  const since = toDate(task.createdAt);
  const days = since ? Math.floor(Math.max(0, now - since.getTime()) / DAY) : 0;
  return `waiting on ${who} · ${daysSpan(days)}`;
}

/** "back tomorrow at 9:00 AM" for a snoozed task. */
export function snoozedLabel(
  task: Pick<TaskView, "snoozedUntil">,
  now: number,
  opts: TimeFormatOptions = {},
): string | null {
  const until = task.snoozedUntil ? toDate(task.snoozedUntil) : null;
  if (!until) return null;
  return `back ${relativeDay(until, now, opts)} at ${timeOfDay(until, opts)}`;
}

/** "done today", "done Mon". */
export function doneLabel(
  task: Pick<TaskView, "completedAt">,
  now: number,
  opts: TimeFormatOptions = {},
): string | null {
  return task.completedAt ? `done ${relativeDay(task.completedAt, now, opts)}` : null;
}

export interface MirrorNote {
  text: string;
  /** "note" is the warm attention tone (never red). */
  tone: "plain" | "note";
}

/** What the one-way Todoist mirror did with this task, when it is worth saying. */
export function mirrorNotes(task: Pick<TaskView, "mirror">): MirrorNote[] {
  const m = task.mirror.todoist;
  if (!m) return [];
  const notes: MirrorNote[] = [];
  if (m.parsedDue) notes.push({ text: `Todoist read this as ${m.parsedDue}`, tone: "plain" });
  if (m.state === "unknown") {
    notes.push({
      text: "Not sure Todoist got the last change. MeeMail checks before sending it again.",
      tone: "note",
    });
  } else if (m.state === "error") {
    const why = m.error?.trim().replace(/[.\s]+$/, "");
    notes.push({
      text: `Todoist didn’t take the last change${why ? `: ${why}` : ""}. The task is safe here.`,
      tone: "note",
    });
  }
  return notes;
}

/** Which Tasks tab shows `task` right now (null for dropped tasks). */
export function taskViewFor(
  task: Pick<TaskView, "status" | "dueAt" | "kind">,
  now: number | Date,
  opts: TimeFormatOptions = {},
): TaskListView | null {
  switch (task.status) {
    case "done":
      return "done";
    case "snoozed":
      return "snoozed";
    case "dropped":
      return null;
    default:
      if (task.dueAt && calendarDayDiff(task.dueAt, now, opts) <= 0) return "today";
      return task.kind === "waiting_on" ? "waiting" : "upcoming";
  }
}

// ---------------------------------------------------------------------------------------------
// Reply tracking

export function businessDaysLabel(days: number): string {
  if (days <= 0) return "since today";
  return days === 1 ? "1 business day" : `${days} business days`;
}

/** "waiting on Lena · 4 business days". */
export function replyWaitingLabel(
  view: Pick<ThreadStatusView, "counterpart" | "waitingBusinessDays">,
): string {
  const who = firstName(view.counterpart);
  return `${who ? `waiting on ${who}` : "waiting for a reply"} · ${businessDaysLabel(view.waitingBusinessDays)}`;
}

/** When MeeMail will bring the thread up again, or a gentle nudge when that time has come. */
export function nudgeLabel(
  view: Pick<ThreadStatusView, "nudgeDueAt">,
  now: number,
  opts: TimeFormatOptions = {},
): string | null {
  const due = view.nudgeDueAt ? toDate(view.nudgeDueAt) : null;
  if (!due) return null;
  if (due.getTime() <= now) return "worth a gentle nudge";
  return `checking again ${relativeDay(due, now, opts)}`;
}

export const REPLY_BADGE: Partial<Record<ThreadStatusView["status"], string>> = {
  to_reply: "To reply",
  awaiting_reply: "Awaiting reply",
};

export function short(text: string, max = 48): string {
  return text.length > max ? `${text.slice(0, max - 3)}…` : text;
}
